'use client';

import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { useToast } from './ui/use-toast';
import { Endpoints } from '@/lib/Endpoints';
import { Requester } from '@/lib/Requester';
import { GenericResponse } from '@/models/GenericResponse';
import { ErrorHandler } from '@/lib/ErrorHandler';
import { reportCheckoutBError } from '@/lib/reportCheckoutBError';
import { getLocalUser } from '@/lib/LocalStorage';
import {
  formatPhoneNumber,
  handlePhoneNumberChange,
  handlePhoneNumberKeyDown,
  phoneRegex,
} from '@/lib/PhoneUtils';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from './ui/form';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Spinner } from './ui/spinner';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { WebLink } from '@/lib/WebLinks';
import { GTMEventType, getBasketEcommerceBody } from '@/lib/GTMEventType';
import { sendGTMEvent } from '@/lib/gtm-wrapper';
import { getBasket, setLastPaymentType } from '@/lib/BasketStorage';
import { AB_KEYS, isVariant, refreshExperiments } from '@/lib/abExperiments';
import { PaymentType } from '@/models/PaymentType';
import { Checkbox } from './ui/checkbox';
import PrivacyDialog from './PrivacyDialog';
import {
  JuzdanCard,
  JuzdanCardsResponse,
  JuzdanCheckResponse,
  JuzdanPayFinishResponse,
  JuzdanPayInitResponse,
} from '@/models/Juzdan';

const TCKN_KEY = 'hzc_juzdan_tckn';
const GSM_KEY = 'hzc_juzdan_gsm';

const FormSchema = z.object({
  identityNumber: z.string().regex(/^\d{11}$/, 'TCKN 11 haneli olmalı'),
  gsm: z
    .string()
    .transform((val) => val.replace(/\D/g, ''))
    .refine((val) => phoneRegex.test(val), {
      message: 'Telefon numaranızı doğru bir biçimde giriniz',
    }),
});

type FormValues = z.infer<typeof FormSchema>;

function logJuzdan(
  checkoutB: boolean | undefined,
  action: string,
  message: string,
  kind: 'api' | 'validation' | 'unexpected' = 'api',
  error?: unknown,
) {
  reportCheckoutBError({
    flow: checkoutB ? 'checkout-b' : 'checkout-a',
    step: 'payment',
    action,
    kind,
    message,
    error,
  });
}

function cardLabel(card: JuzdanCard) {
  const name = card.cardName || card.cardAlias || 'Kart';
  const mask = card.maskedCardNumber || card.cardNumberMask || '';
  return mask ? `${name} · ${mask}` : name;
}

function isMobileDevice() {
  return /Android|iPhone|iPad/i.test(navigator.userAgent);
}

export const JUZDAN_PAYMENT_TYPE: PaymentType = {
  value: 12,
  label: 'Juzdan',
  color: 'red',
  type: 'akbank_juzdan',
};

export function withJuzdanMethod(types: PaymentType[], show: boolean): PaymentType[] {
  const rest = types.filter((type) => type.type !== 'akbank_juzdan');
  if (!show) return rest;
  return [...rest, types.find((type) => type.type === 'akbank_juzdan') || JUZDAN_PAYMENT_TYPE];
}

export function useShowJuzdan() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const sync = () => {
      if (!cancelled) setShow(isVariant(AB_KEYS.juzdan, 'b'));
    };
    void refreshExperiments().finally(sync);
    window.addEventListener('hzc:ab-experiments-updated', sync);
    return () => {
      cancelled = true;
      window.removeEventListener('hzc:ab-experiments-updated', sync);
    };
  }, []);
  return show;
}

export function CheckoutPaymentTypeJuzdan({ checkoutB }: { checkoutB?: boolean }) {
  const router = useRouter();
  const { toast } = useToast();
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<'form' | 'pairing' | 'cards'>('form');
  const [sessionToken, setSessionToken] = useState('');
  const [redirectLink, setRedirectLink] = useState('');
  const [redirectWebUrl, setRedirectWebUrl] = useState('');
  const [cards, setCards] = useState<JuzdanCard[]>([]);
  const [editCardsLink, setEditCardsLink] = useState('');
  const [cardId, setCardId] = useState('');
  const [otpOpen, setOtpOpen] = useState(false);
  const [otp, setOtp] = useState('');
  const pending = useRef<JuzdanPayInitResponse | null>(null);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { identityNumber: '', gsm: '' },
  });

  const showError = (action: string, error: unknown) => {
    const { message } = ErrorHandler.getMessage(error);
    logJuzdan(checkoutB, action, message, 'api', error);
    toast({ variant: 'destructive', description: message });
  };

  const loadCards = async (identityNumber: string, gsm: string, token: string) => {
    const { data: result } = await Requester.post<GenericResponse<JuzdanCardsResponse>>(
      Endpoints.JUZDAN_CARDS,
      { identityNumber, gsm, sessionToken: token },
    );
    const list = result?.data?.customerCardList || [];
    setCards(list);
    setEditCardsLink(result?.data?.editCardsLink || '');
    setCardId(list[0]?.cardId ? String(list[0].cardId) : '');
    if (result?.data?.sessionToken) setSessionToken(result.data.sessionToken);
    setPhase('cards');
  };

  const runCheck = async (identityNumber: string, gsm: string) => {
    sessionStorage.setItem(TCKN_KEY, identityNumber);
    sessionStorage.setItem(GSM_KEY, gsm);
    const { data: result } = await Requester.post<GenericResponse<JuzdanCheckResponse>>(
      Endpoints.JUZDAN_CHECK,
      { identityNumber, gsm, source: 'web' },
    );
    const data = result?.data;
    if (data?.sessionToken) setSessionToken(data.sessionToken);
    if (data?.isPaired && data.sessionToken) {
      await loadCards(identityNumber, gsm, data.sessionToken);
      return;
    }
    setRedirectLink(data?.redirectLink || '');
    setRedirectWebUrl(data?.redirectWebUrl || '');
    setPhase('pairing');
  };

  useEffect(() => {
    const localUser = getLocalUser();
    const storedTckn = sessionStorage.getItem(TCKN_KEY) || '';
    if (storedTckn) form.setValue('identityNumber', storedTckn);
    if (localUser?.msisdn) {
      try {
        form.setValue('gsm', formatPhoneNumber(localUser.msisdn.replace('+90', '')));
      } catch {
        // keep empty
      }
    }
    const returning = new URLSearchParams(window.location.search).get('juzdan') === '1';
    const storedGsm = sessionStorage.getItem(GSM_KEY) || '';
    if (returning && storedTckn && storedGsm) {
      const gsm = storedGsm.replace(/\D/g, '').slice(-10);
      setLoading(true);
      runCheck(storedTckn, gsm)
        .catch((error) => showError('juzdan-return-check', error))
        .finally(() => setLoading(false));
    }
    // form defaults are applied once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || phaseRef.current !== 'pairing') return;
      const identityNumber = sessionStorage.getItem(TCKN_KEY) || '';
      const gsm = (form.getValues('gsm') || '').replace(/\D/g, '').slice(-10);
      if (!identityNumber || gsm.length < 10) return;
      setLoading(true);
      runCheck(identityNumber, gsm)
        .catch((error) => showError('juzdan-recheck', error))
        .finally(() => setLoading(false));
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmit(values: FormValues) {
    if (loading) return;
    if (!privacyChecked) {
      logJuzdan(checkoutB, 'juzdan-privacy', 'Satış sözleşmesini kabul etmelisiniz', 'validation');
      toast({ variant: 'destructive', description: 'Satış sözleşmesini kabul etmelisiniz' });
      return;
    }
    setLoading(true);
    try {
      await runCheck(values.identityNumber, values.gsm);
    } catch (error) {
      showError('juzdan-check', error);
    } finally {
      setLoading(false);
    }
  }

  function openJuzdan() {
    if (isMobileDevice() && redirectLink) {
      window.location.href = redirectLink;
      return;
    }
    if (redirectWebUrl) {
      window.open(redirectWebUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (redirectLink) window.location.href = redirectLink;
  }

  async function finishPayment(init: JuzdanPayInitResponse, otpCode?: string) {
    const identityNumber = form.getValues('identityNumber');
    const gsm = form.getValues('gsm');
    const { data: result } = await Requester.post<GenericResponse<JuzdanPayFinishResponse>>(
      Endpoints.JUZDAN_PAY_FINISH,
      {
        identityNumber,
        gsm,
        sessionToken,
        cardId,
        transactionId: init.transactionId,
        merchantOrderId: init.merchantOrderId,
        amount: init.amount,
        ...(otpCode ? { otpCode } : {}),
      },
    );
    if (result?.data?.txnStatus === '00' || result?.meta?.statusCode === 0) {
      router.replace(WebLink.ORDER_RECEIVED);
    }
  }

  async function pay() {
    if (loading || !cardId) return;
    const basket = getBasket();
    setLastPaymentType('Juzdan');
    if (basket) {
      sendGTMEvent({
        event: GTMEventType.ADD_PAYMENT_INFO,
        ecommerce: {
          ...getBasketEcommerceBody(basket),
          payment_type: 'Juzdan',
        },
      });
    }
    setLoading(true);
    try {
      const { data: result } = await Requester.post<GenericResponse<JuzdanPayInitResponse>>(
        Endpoints.JUZDAN_PAY_INIT,
        {
          identityNumber: form.getValues('identityNumber'),
          gsm: form.getValues('gsm'),
          sessionToken,
          cardId,
        },
      );
      const init = result?.data;
      if (!init?.transactionId) return;
      if (init.sessionToken) setSessionToken(init.sessionToken);
      if (init.otpFlag) {
        pending.current = init;
        setOtp('');
        setOtpOpen(true);
        return;
      }
      await finishPayment(init);
    } catch (error) {
      showError('juzdan-pay', error);
    } finally {
      setLoading(false);
    }
  }

  async function confirmOtp() {
    if (!pending.current || otp.length < 4) {
      toast({ variant: 'destructive', description: 'Şifreyi eksiksiz giriniz' });
      return;
    }
    setLoading(true);
    try {
      await finishPayment(pending.current, otp);
      setOtpOpen(false);
    } catch (error) {
      showError('juzdan-otp', error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-[#E7E4DC] bg-white p-4">
      {phase === 'form' ? (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="identityNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>T.C. Kimlik No</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      inputMode="numeric"
                      maxLength={11}
                      placeholder="11 haneli TCKN"
                      className="h-[44px]"
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="gsm"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cep telefonu</FormLabel>
                  <FormControl>
                    <Input
                      value={field.value}
                      placeholder="(5__) ___-____"
                      className="h-[44px]"
                      onChange={(e) => handlePhoneNumberChange(field.onChange, e)}
                      onKeyDown={(e) => handlePhoneNumberKeyDown(field.onChange, e)}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <div className="flex items-center gap-2">
              <Checkbox
                className="h-4 w-4"
                checked={privacyChecked}
                onCheckedChange={(isChecked) => setPrivacyChecked(isChecked === true)}
              />
              <span className="text-sm text-[#212121]">
                <button type="button" className="font-medium underline" onClick={() => setPrivacyOpen(true)}>
                  Satış Sözleşmesi
                </button>
                ’ni okudum ve kabul ediyorum.
              </span>
              <PrivacyDialog open={privacyOpen} onOpenChange={setPrivacyOpen} />
            </div>
            <Button type="submit" className="w-full rounded-full bg-[#2B3280] hover:bg-[#2B3280]" disabled={loading}>
              {loading ? <Spinner className="size-5 text-white" /> : 'Devam'}
            </Button>
          </form>
        </Form>
      ) : null}

      {phase === 'pairing' ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-[#212121]">
            Juzdan uygulamasında eşleşmeyi tamamlayın. Bu sayfaya döndüğünüzde kartlarınız listelenir.
          </p>
          <Button type="button" className="w-full rounded-full bg-[#2B3280] hover:bg-[#2B3280]" onClick={openJuzdan}>
            Juzdan’da eşleştir
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full rounded-full"
            disabled={loading}
            onClick={() => {
              const identityNumber = form.getValues('identityNumber');
              const gsm = form.getValues('gsm');
              setLoading(true);
              runCheck(identityNumber, gsm)
                .catch((error) => showError('juzdan-recheck', error))
                .finally(() => setLoading(false));
            }}
          >
            {loading ? <Spinner className="size-5" /> : 'Eşleşmeyi tamamladım'}
          </Button>
        </div>
      ) : null}

      {phase === 'cards' ? (
        <div className="flex flex-col gap-3">
          {cards.length > 0 ? (
            <RadioGroup value={cardId} onValueChange={setCardId}>
              {cards.map((card) => (
                <label key={String(card.cardId)} className="flex items-center gap-3 rounded-xl border border-[#E7E4DC] px-3 py-2">
                  <RadioGroupItem value={String(card.cardId)} />
                  <span className="text-sm text-[#212121]">{cardLabel(card)}</span>
                </label>
              ))}
            </RadioGroup>
          ) : (
            <p className="text-sm text-[#212121]">Juzdan’da kayıtlı kart görünmüyor.</p>
          )}
          {editCardsLink ? (
            <a href={editCardsLink} target="_blank" rel="noreferrer" className="text-sm font-semibold text-[#2B3280] underline">
              Juzdan’da kart ekle
            </a>
          ) : null}
          <Button
            type="button"
            className="w-full rounded-full bg-[#2B3280] hover:bg-[#2B3280]"
            disabled={loading || !cardId}
            onClick={() => { void pay(); }}
          >
            {loading ? <Spinner className="size-5 text-white" /> : 'Güvenli Ödeme Yap'}
          </Button>
        </div>
      ) : null}

      <Dialog open={otpOpen} onOpenChange={setOtpOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Juzdan doğrulama</DialogTitle>
          </DialogHeader>
          <InputOTP maxLength={6} value={otp} onChange={setOtp}>
            <InputOTPGroup className="mx-auto">
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
          <DialogFooter>
            <Button type="button" className="rounded-full bg-[#2B3280] hover:bg-[#2B3280]" onClick={() => { void confirmOtp(); }}>
              {loading ? <Spinner className="size-5 text-white" /> : 'Onayla'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
